import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Euro, 
  Users, 
  MessageSquare, 
  Pause, 
  Plus, 
  Upload, 
  Trash2,
  Play,
  Send,
  RefreshCw,
  Smartphone,
  Edit
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_debt: 0, active_debts: 0, paused_debts: 0, messages_sent: 0 });
  const [debts, setDebts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [whatsappStatus, setWhatsappStatus] = useState({ connected: false, qr_code: null });
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [addDebtOpen, setAddDebtOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [editDebtOpen, setEditDebtOpen] = useState(false);
  
  // Form states
  const [newDebt, setNewDebt] = useState({ contact_id: "", amount: "", daily_interest_rate: 0, description: "" });
  const [newContact, setNewContact] = useState({ name: "", phone: "" });
  const [editingDebt, setEditingDebt] = useState(null);
  const [editDebtData, setEditDebtData] = useState({ daily_interest_rate: 0, description: "", current_amount: 0 });
  const [dragActive, setDragActive] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, debtsRes, contactsRes, messagesRes, waRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/debts`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/messages`),
        axios.get(`${API}/whatsapp/status`)
      ]);
      setStats(statsRes.data);
      setDebts(debtsRes.data);
      setContacts(contactsRes.data);
      setMessages(messagesRes.data.slice(0, 10));
      setWhatsappStatus(waRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleAddDebt = async () => {
    if (!newDebt.contact_id || !newDebt.amount) {
      toast.error("Seleziona un contatto e inserisci l'importo");
      return;
    }
    try {
      await axios.post(`${API}/debts`, {
        contact_id: newDebt.contact_id,
        amount: parseFloat(newDebt.amount),
        daily_interest_rate: newDebt.daily_interest_rate,
        description: newDebt.description
      });
      toast.success("Debito aggiunto!");
      setAddDebtOpen(false);
      setNewDebt({ contact_id: "", amount: "", daily_interest_rate: 0, description: "" });
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiunta del debito");
    }
  };

  const handleAddContact = async () => {
    if (!newContact.name || !newContact.phone) {
      toast.error("Inserisci nome e telefono");
      return;
    }
    try {
      await axios.post(`${API}/contacts`, newContact);
      toast.success("Contatto aggiunto!");
      setAddContactOpen(false);
      setNewContact({ name: "", phone: "" });
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiunta del contatto");
    }
  };

  const handleImportVCF = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post(`${API}/contacts/import-vcf`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`Importati ${response.data.imported} contatti!`);
      setImportOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'importazione");
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImportVCF(e.dataTransfer.files[0]);
    }
  };

  const togglePause = async (debtId, currentPaused) => {
    try {
      await axios.patch(`${API}/debts/${debtId}`, { is_paused: !currentPaused });
      toast.success(currentPaused ? "Promemoria riattivati" : "Promemoria in pausa");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const deleteDebt = async (debtId) => {
    try {
      await axios.delete(`${API}/debts/${debtId}`);
      toast.success("Debito eliminato");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const openEditDebt = (debt) => {
    setEditingDebt(debt);
    setEditDebtData({
      daily_interest_rate: debt.daily_interest_rate,
      description: debt.description,
      current_amount: debt.current_amount
    });
    setEditDebtOpen(true);
  };

  const handleUpdateDebt = async () => {
    if (!editingDebt) return;
    try {
      await axios.patch(`${API}/debts/${editingDebt.id}`, editDebtData);
      toast.success("Debito aggiornato!");
      setEditDebtOpen(false);
      setEditingDebt(null);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const sendMessage = async (debtId) => {
    try {
      const response = await axios.post(`${API}/messages/send/${debtId}`);
      if (response.data.status === "sent") {
        toast.success("Messaggio inviato!");
      } else {
        toast.error("Invio fallito. Controlla la connessione WhatsApp");
      }
      fetchData();
    } catch (error) {
      toast.error("Errore nell'invio");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8 lg:p-12 relative noise-overlay">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
            Bentornato
          </h1>
          <p className="text-muted-foreground mt-1">Gestisci i tuoi crediti</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border">
            <Smartphone className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">WhatsApp</span>
            <span className={whatsappStatus.connected ? "status-online" : "status-offline"} data-testid="whatsapp-status-indicator" />
          </div>
          <Button onClick={fetchData} variant="ghost" size="icon" data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {/* Total Debt Card */}
        <Card className="col-span-full md:col-span-2 bg-card border-border card-hover bento-item" data-testid="total-debt-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-muted-foreground text-sm font-medium flex items-center gap-2">
              <Euro className="h-4 w-4" />
              Totale Crediti
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-mono text-5xl md:text-6xl font-bold tracking-tight text-primary glow-green">
              €{stats.total_debt.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp Status */}
        <Card className="bg-card border-border card-hover bento-item" data-testid="whatsapp-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-muted-foreground text-sm font-medium">WhatsApp</CardTitle>
          </CardHeader>
          <CardContent>
            {whatsappStatus.connected ? (
              <div className="flex flex-col gap-2">
                <Badge className="bg-primary/20 text-primary border-primary/30 w-fit">Connesso</Badge>
                <p className="text-xs text-muted-foreground">Pronto per inviare</p>
              </div>
            ) : whatsappStatus.qr_code ? (
              <div className="flex flex-col items-center gap-2">
                <img src={whatsappStatus.qr_code} alt="QR Code" className="w-32 h-32 rounded-lg" />
                <p className="text-xs text-muted-foreground text-center">Scansiona con WhatsApp</p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Badge variant="destructive" className="w-fit">Disconnesso</Badge>
                <p className="text-xs text-muted-foreground">Avvia il servizio WhatsApp</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-card border-border card-hover bento-item" data-testid="quick-actions-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-muted-foreground text-sm font-medium">Azioni Rapide</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <Button 
              className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-full h-12" 
              onClick={() => navigate("/contacts")}
              data-testid="manage-contacts-btn"
            >
              <Users className="h-4 w-4 mr-2" />
              Gestione Contatti
            </Button>
            <Dialog open={addDebtOpen} onOpenChange={setAddDebtOpen}>
              <DialogTrigger asChild>
                <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-12" data-testid="add-debt-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuovo Debito
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border sm:max-w-[425px] w-[95vw]">
                <DialogHeader>
                  <DialogTitle>Aggiungi Debito</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Contatto</Label>
                    <Select 
                      value={newDebt.contact_id} 
                      onValueChange={(v) => setNewDebt({...newDebt, contact_id: v})}
                    >
                      <SelectTrigger data-testid="contact-select" className="h-12">
                        <SelectValue placeholder="Seleziona contatto" />
                      </SelectTrigger>
                      <SelectContent>
                        {contacts.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name} ({c.phone})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Importo (€)</Label>
                    <Input 
                      type="number" 
                      placeholder="0.00"
                      value={newDebt.amount}
                      onChange={(e) => setNewDebt({...newDebt, amount: e.target.value})}
                      className="font-mono text-lg h-12"
                      data-testid="amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Interesse Giornaliero: {newDebt.daily_interest_rate}%</Label>
                    <Slider
                      value={[newDebt.daily_interest_rate]}
                      onValueChange={(v) => setNewDebt({...newDebt, daily_interest_rate: v[0]})}
                      max={5}
                      step={0.1}
                      className="py-4"
                      data-testid="interest-slider"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Descrizione</Label>
                    <Input 
                      placeholder="Motivo del debito..."
                      value={newDebt.description}
                      onChange={(e) => setNewDebt({...newDebt, description: e.target.value})}
                      className="h-12"
                      data-testid="description-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={handleAddDebt} className="bg-primary text-primary-foreground rounded-full h-12 w-full sm:w-auto" data-testid="confirm-add-debt-btn">
                    Aggiungi
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={importOpen} onOpenChange={setImportOpen}>
              <DialogTrigger asChild>
                <Button variant="secondary" className="w-full rounded-full h-12" data-testid="import-vcf-btn">
                  <Upload className="h-4 w-4 mr-2" />
                  Importa VCF
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border sm:max-w-[425px] w-[95vw]">
                <DialogHeader>
                  <DialogTitle>Importa Contatti</DialogTitle>
                </DialogHeader>
                <div 
                  className={`drop-zone text-center cursor-pointer ${dragActive ? 'active' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('vcf-input').click()}
                  data-testid="vcf-dropzone"
                >
                  <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">Trascina il file VCF qui</p>
                  <p className="text-sm text-muted-foreground/60 mt-1">oppure clicca per selezionare</p>
                  <input 
                    id="vcf-input"
                    type="file" 
                    accept=".vcf"
                    className="hidden"
                    onChange={(e) => e.target.files[0] && handleImportVCF(e.target.files[0])}
                  />
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={addContactOpen} onOpenChange={setAddContactOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" className="w-full rounded-full h-12" data-testid="add-contact-btn">
                  <Users className="h-4 w-4 mr-2" />
                  Aggiungi Contatto
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border sm:max-w-[425px] w-[95vw]">
                <DialogHeader>
                  <DialogTitle>Nuovo Contatto</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Nome</Label>
                    <Input 
                      placeholder="Mario Rossi"
                      value={newContact.name}
                      onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                      className="h-12"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefono</Label>
                    <Input 
                      placeholder="+39..."
                      value={newContact.phone}
                      onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                      className="h-12"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={handleAddContact} className="bg-primary text-primary-foreground rounded-full h-12 w-full sm:w-auto" data-testid="confirm-add-contact-btn">
                    Aggiungi
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        {/* Debt List */}
        <Card className="col-span-full md:col-span-2 lg:col-span-3 bg-card border-border row-span-2" data-testid="debt-list-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Lista Debitori
              <Badge variant="secondary" className="ml-2">{stats.active_debts}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] pr-4">
              {debts.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun debito registrato</p>
                  <p className="text-sm mt-1">Aggiungi il primo debito per iniziare</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {debts.map((debt, index) => (
                    <div 
                      key={debt.id}
                      className={`p-5 sm:p-4 rounded-xl border card-hover animate-fadeIn cursor-pointer touch-manipulation ${
                        debt.is_paused 
                          ? 'bg-yellow-500/10 border-yellow-500/50' 
                          : 'bg-secondary/50 border-border'
                      }`}
                      style={{ animationDelay: `${index * 50}ms` }}
                      onClick={() => navigate(`/debt/${debt.id}`)}
                      data-testid={`debt-item-${debt.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-base sm:text-lg">{debt.contact_name}</h3>
                            {debt.is_paused && (
                              <Badge variant="outline" className="text-yellow-600 border-yellow-500/50 bg-yellow-500/20">
                                <Pause className="h-3 w-3 mr-1" />
                                In Pausa
                              </Badge>
                            )}
                            {debt.daily_interest_rate > 0 && (
                              <Badge variant="outline" className="text-accent border-accent/50">
                                +{debt.daily_interest_rate}%/giorno
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 break-all">{debt.contact_phone}</p>
                          {debt.description && (
                            <p className="text-xs text-muted-foreground/70 mt-1 line-clamp-2">{debt.description}</p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="font-mono text-xl sm:text-2xl font-bold text-destructive">
                            €{debt.current_amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                          </div>
                          {debt.current_amount !== debt.original_amount && (
                            <p className="text-xs text-muted-foreground line-through">
                              €{debt.original_amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4 justify-end flex-wrap" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => openEditDebt(debt)}
                          className="h-11 sm:h-9 min-w-[48px] sm:min-w-[44px] touch-manipulation"
                          data-testid={`edit-btn-${debt.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => togglePause(debt.id, debt.is_paused)}
                          className="h-11 sm:h-9 min-w-[48px] sm:min-w-[44px] touch-manipulation"
                          data-testid={`pause-btn-${debt.id}`}
                        >
                          {debt.is_paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => sendMessage(debt.id)}
                          className="text-primary hover:text-primary h-11 sm:h-9 min-w-[48px] sm:min-w-[44px] touch-manipulation"
                          data-testid={`send-btn-${debt.id}`}
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => deleteDebt(debt.id)}
                          className="text-destructive hover:text-destructive h-11 sm:h-9 min-w-[48px] sm:min-w-[44px] touch-manipulation"
                          data-testid={`delete-btn-${debt.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="col-span-full md:col-span-1 bg-card border-border row-span-2" data-testid="activity-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              Attività Recente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              {messages.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nessun messaggio</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg, index) => (
                    <div 
                      key={msg.id} 
                      className="p-3 rounded-lg bg-secondary/30 animate-fadeIn"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{msg.contact_name}</span>
                        <Badge 
                          variant={msg.status === "sent" ? "default" : "destructive"}
                          className={msg.status === "sent" ? "bg-primary/20 text-primary" : ""}
                        >
                          {msg.status === "sent" ? "Inviato" : "Fallito"}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{msg.message}</p>
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        {new Date(msg.created_at).toLocaleString('it-IT')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Stats Cards - Mobile Optimized */}
        <div className="col-span-full grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
          <Card className="bg-card border-border card-hover bento-item" data-testid="active-debts-stat">
            <CardContent className="pt-4 md:pt-6 px-3 md:px-6">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Users className="h-6 w-6 md:h-8 md:w-8 text-primary opacity-50" />
                </div>
                <div>
                  <p className="text-xs md:text-sm text-muted-foreground">Debiti Attivi</p>
                  <p className="font-mono text-2xl md:text-3xl lg:text-4xl font-bold">{stats.active_debts}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`bg-card border-border card-hover bento-item ${stats.paused_debts > 0 ? 'ring-2 ring-yellow-500/30' : ''}`} data-testid="paused-stat">
            <CardContent className="pt-4 md:pt-6 px-3 md:px-6">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Pause className={`h-6 w-6 md:h-8 md:w-8 opacity-50 ${stats.paused_debts > 0 ? 'text-yellow-600' : 'text-yellow-500'}`} />
                </div>
                <div>
                  <p className="text-xs md:text-sm text-muted-foreground">In Pausa</p>
                  <p className={`font-mono text-2xl md:text-3xl lg:text-4xl font-bold ${stats.paused_debts > 0 ? 'text-yellow-600' : ''}`}>
                    {stats.paused_debts}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border card-hover bento-item" data-testid="messages-stat">
            <CardContent className="pt-4 md:pt-6 px-3 md:px-6">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <MessageSquare className="h-6 w-6 md:h-8 md:w-8 text-primary opacity-50" />
                </div>
                <div>
                  <p className="text-xs md:text-sm text-muted-foreground">Messaggi</p>
                  <p className="font-mono text-2xl md:text-3xl lg:text-4xl font-bold">{stats.messages_sent}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border card-hover bento-item" data-testid="contacts-stat">
            <CardContent className="pt-4 md:pt-6 px-3 md:px-6">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Users className="h-6 w-6 md:h-8 md:w-8 text-primary opacity-50" />
                </div>
                <div>
                  <p className="text-xs md:text-sm text-muted-foreground">Contatti</p>
                  <p className="font-mono text-2xl md:text-3xl lg:text-4xl font-bold">{contacts.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Debt Dialog */}
      <Dialog open={editDebtOpen} onOpenChange={setEditDebtOpen}>
        <DialogContent className="bg-card border-border sm:max-w-[500px] w-[95vw]">
          <DialogHeader>
            <DialogTitle>Modifica Debito</DialogTitle>
          </DialogHeader>
          {editingDebt && (
            <div className="space-y-4 py-4">
              <div className="p-3 rounded-lg bg-secondary/50">
                <p className="text-sm font-medium">{editingDebt.contact_name}</p>
                <p className="text-xs text-muted-foreground">{editingDebt.contact_phone}</p>
              </div>
              <div className="space-y-2">
                <Label>Importo Attuale (€)</Label>
                <Input 
                  type="number" 
                  placeholder="0.00"
                  value={editDebtData.current_amount}
                  onChange={(e) => setEditDebtData({...editDebtData, current_amount: parseFloat(e.target.value) || 0})}
                  className="font-mono text-lg h-12"
                />
              </div>
              <div className="space-y-2">
                <Label>Interesse Giornaliero: {editDebtData.daily_interest_rate}%</Label>
                <Slider
                  value={[editDebtData.daily_interest_rate]}
                  onValueChange={(v) => setEditDebtData({...editDebtData, daily_interest_rate: v[0]})}
                  max={5}
                  step={0.1}
                  className="py-4"
                />
              </div>
              <div className="space-y-2">
                <Label>Descrizione</Label>
                <Input 
                  placeholder="Motivo del debito..."
                  value={editDebtData.description}
                  onChange={(e) => setEditDebtData({...editDebtData, description: e.target.value})}
                  className="h-12"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={handleUpdateDebt} className="bg-primary text-primary-foreground rounded-full h-12 w-full sm:w-auto">
              Salva Modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

